import { createContext, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@components/modals/confirm-modal/src/confirm-modal';
import { Modal } from '@components/modals/modal';

import { ModalContextValue, ModalTypes } from './modal-context.type';
import { ACTION_MODALS } from './utils';

export const ModalContext = createContext<ModalContextValue>({
  currentModalType: '',
  toggleModal: () => {},
  setPreventExit: () => {},
  handleCloseModal: () => {},
});

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [currentModalType, setCurrentModalType] = useState<ModalTypes>('');
  const [preventExit, setPreventExit] = useState<boolean>(false);

  const toggleModal = useCallback(
    ({ type, isOpen }: { type: ModalTypes; isOpen?: boolean }) => {
      setCurrentModalType(type);
      setIsModalOpen(isOpen ?? !isModalOpen);
    },
    [isModalOpen]
  );

  const handleCloseModal = useCallback(() => {
    if (preventExit) {
      setIsConfirmModalOpen(true);
    } else {
      toggleModal({ type: '', isOpen: false });
    }
  }, [preventExit, toggleModal]);

  const handleConfirmModal = () => {
    toggleModal({ type: '', isOpen: false });
    setIsConfirmModalOpen(false);
  };

  const ModalComponent = useCallback(() => {
    if (!currentModalType) return null;

    const CurrentModal = ACTION_MODALS[currentModalType].component();

    return CurrentModal;
  }, [currentModalType]);

  return (
    <ModalContext.Provider value={{ currentModalType, toggleModal, setPreventExit, handleCloseModal }}>
      <Modal isOpen={isModalOpen} handleClose={handleCloseModal}>
        <ModalComponent />
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          title={t('offers:admissionModalConfirmTitle')}
          handleClickConfirm={handleConfirmModal}
          handleClose={() => setIsConfirmModalOpen(false)}
        />
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};
